import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  ChevronRight,
  Home,
  DollarSign,
  Users,
  Building,
  MapPin,
  Target,
  TrendingUp,
  Activity,
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Phone,
  Mail,
  Video,
  UserCheck,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  PhoneOff,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Headphones,
  MessageSquare,
  Zap,
  Star,
  AlertCircle,
  Info
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import { Breadcrumb } from '../../components/Common'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { callsService } from '../../services/callsService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const Calls = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalCalls: 0,
    incomingCalls: 0,
    outgoingCalls: 0,
    missedCalls: 0,
    completedCalls: 0,
    scheduledCalls: 0,
    followUpCalls: 0,
    totalDuration: 0,
    averageDuration: 0,
    conversionRate: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Transactions',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Calls',
      path: '/crm/calls',
      isCurrent: true
    }
  ]

  // Define columns for the data table
  const columns = [
    {
      key: 'callCode',
      label: 'Call Code',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <PhoneCall size={14} className="text-blue-600" />
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'callerName',
      label: 'Caller Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value || 'N/A'}</div>
          {row.callerCompany && (
            <div className="text-sm text-gray-500">{row.callerCompany}</div>
          )}
        </div>
      )
    },
    {
      key: 'callType',
      label: 'Call Type',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value || 'N/A'}
        </span>
      )
    },
    {
      key: 'callStatus',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Scheduled': 'bg-blue-100 text-blue-800',
          'In Progress': 'bg-yellow-100 text-yellow-800',
          'Completed': 'bg-green-100 text-green-800',
          'Missed': 'bg-red-100 text-red-800',
          'Cancelled': 'bg-gray-100 text-gray-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value) => {
        const priorityColors = {
          'High': 'bg-red-100 text-red-800',
          'Medium': 'bg-yellow-100 text-yellow-800',
          'Low': 'bg-green-100 text-green-800'
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[value] || 'bg-gray-100 text-gray-800'}`}>
            {value}
          </span>
        )
      }
    },
    {
      key: 'callDate',
      label: 'Date',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ]

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      loadData()
      hasLoadedRef.current = true
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [callsResponse, statsResponse] = await Promise.all([
        callsService.getAllCalls(),
        callsService.getCallStats()
      ])

      if (callsResponse.success) {
        setData(callsResponse.data.docs || callsResponse.data || [])
        
        if ((callsResponse.data.docs || callsResponse.data || []).length === 0 && !hasShownEmptyToastRef.current) {
          toast.info('No calls found. Create your first call to get started.')
          hasShownEmptyToastRef.current = true
        }
      } else {
        toast.error(callsResponse.message || 'Failed to load calls')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data)
      }
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Failed to load call data')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    navigate('/crm/calls/new')
  }

  const handleEdit = (row) => {
    navigate('/crm/calls/edit', { 
      state: { callData: row } 
    })
  }

  const handleView = (row) => {
    navigate('/crm/calls/view', { 
      state: { callData: row } 
    })
  }

  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const response = await callsService.deleteCall(deleteItem._id)
      if (response.success) {
        toast.success('Call deleted successfully')
        loadData()
        setShowDeleteModal(false)
        setDeleteItem(null)
      } else {
        toast.error(response.message || 'Failed to delete call')
      }
    } catch (error) {
      console.error('Error deleting call:', error)
      toast.error('Failed to delete call')
    }
  }

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.error('Please select calls to delete')
      return
    }

    try {
      const response = await callsService.bulkDeleteCalls(selectedRows)
      if (response.success) {
        toast.success(response.message)
        setSelectedRows([])
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete calls')
      }
    } catch (error) {
      console.error('Error bulk deleting calls:', error)
      toast.error('Failed to delete calls')
    }
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Call Management</h1>
          <p>Manage and track all your call activities, outcomes, and follow-ups</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAdd}
          >
            <Plus size={16} />
            Add Call
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <PhoneCall size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalCalls || 0}</div>
          <div className="metric-title">Total Calls</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <PhoneIncoming size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.incomingCalls || 0}</div>
          <div className="metric-title">Incoming</div>
          <div className="metric-subtitle">Currently tracking</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <PhoneOutgoing size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+15%</span>
            </div>
          </div>
          <div className="metric-value">{stats.outgoingCalls || 0}</div>
          <div className="metric-title">Outgoing</div>
          <div className="metric-subtitle">Call progression</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+20%</span>
            </div>
          </div>
          <div className="metric-value">{stats.completedCalls || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">Success rate</div>
        </div>
      </div>

      {/* Data Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onBulkDelete={handleBulkDelete}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search calls..."
          emptyMessage="No calls found. Create your first call to get started."
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Call"
        message={`Are you sure you want to delete call "${deleteItem?.callCode}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default Calls
